import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { X, Save, Loader2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORO = '#c9a84c';

// Pasar un anuncio de portal a ficha de vendedor.
//
// Los campos son EXACTAMENTE los de la ficha de vendedor del CRM, no una
// version reducida: el embudo real es nuevo → contactado → visita_agendada →
// exclusiva_firmada → cerrado, y la motivación es un nivel, no una frase. Si
// aquí se pusieran otros, la ficha nacería distinta a las demás y las alertas
// de seguimiento no sabrían qué hacer con ella.
//
// El scraping trae precio, metros, habitaciones, zona y teléfono. Lo que nunca
// trae — cómo se llama el dueño, por qué vende — lo pone una persona.

const ESTADOS = [
  ['nuevo',             'Nuevo — primer contacto pendiente'],
  ['contactado',        'Contactado — hay que agendar visita'],
  ['visita_agendada',   'Visita agendada'],
  ['exclusiva_firmada', 'Exclusiva firmada'],
  ['cerrado',           'Cerrado'],
  ['descartado',        'Descartado'],
];

const MOTIVACIONES = [
  ['alta',  'Alta — tiene prisa por vender'],
  ['media', 'Media'],
  ['baja',  'Baja — no corre prisa'],
];

const clase = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 " +
              "placeholder-white/25 focus:outline-none focus:border-[#c9a84c]/50";

const Campo = ({ etiqueta, ayuda, children, ancho = '' }) => (
  <div className={ancho}>
    <label className="block text-white/40 text-[11px] uppercase tracking-wide mb-1">{etiqueta}</label>
    {children}
    {ayuda && <p className="text-white/25 text-[10px] mt-1">{ayuda}</p>}
  </div>
);

export function FichaDesdeAnuncio({ anuncio, orgId, onCerrar, onHecho }) {
  const crudos = anuncio.datos_crudos || {};
  const nombreAnuncio = anuncio.anunciante_nombre && anuncio.anunciante_nombre !== '-'
    ? anuncio.anunciante_nombre : '';

  const [f, setF] = useState({
    nombre: nombreAnuncio,
    telefono: crudos.telefono || '',
    email: '',
    zona: [anuncio.zona, anuncio.municipio].filter(Boolean).join(', '),
    tipo_vivienda: anuncio.tipo_vivienda || '',
    precio_estimado: anuncio.precio ?? '',
    comision_ofertada: '',
    motivacion: 'media',
    estado: 'nuevo',
    fecha_visita: '',
    notes: '',
    crear_propiedad: true,
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const ganancia = f.precio_estimado && f.comision_ofertada
    ? Math.round(Number(f.precio_estimado) * Number(f.comision_ofertada) / 100)
    : null;

  const guardar = async () => {
    if (!f.nombre.trim()) {
      toast.error('La ficha necesita un nombre');
      return;
    }
    setGuardando(true);
    try {
      const r = await axios.post(
        `${API}/crm/portales/listings/${anuncio.listing_id}/a-vendedor`,
        { ...f, org_id: orgId,
          precio_estimado: f.precio_estimado === '' ? null : Number(f.precio_estimado),
          comision_ofertada: f.comision_ofertada === '' ? null : Number(f.comision_ofertada) });
      toast.success(r.data?.ya_existia
        ? 'Ese propietario ya tenía ficha; se ha enlazado con ella.'
        : 'Ficha de vendedor creada.');
      onHecho();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : (d || 'No se pudo crear la ficha'));
    }
    setGuardando(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start md:items-center justify-center p-3 overflow-y-auto"
      onClick={onCerrar}>
      <motion.div initial={{ scale: 0.97, y: 10 }} animate={{ scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-2xl my-6 shadow-2xl">

        <div className="flex items-start gap-3 p-5 border-b border-white/8">
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-bold text-lg">Nueva ficha de vendedor</h3>
            <p className="text-white/40 text-xs mt-0.5 truncate">
              {anuncio.titulo}
              {anuncio.origen && <span className="ml-2 px-1.5 py-0.5 rounded bg-white/8 text-[10px]">{anuncio.origen}</span>}
              {anuncio.dias_en_mercado ? <span className="ml-2">· {anuncio.dias_en_mercado} días publicado</span> : null}
            </p>
            {anuncio.url && (
              <a href={anuncio.url} target="_blank" rel="noreferrer"
                 className="text-[#c9a84c] text-[11px] hover:underline flex items-center gap-1 mt-1">
                ver el anuncio <ExternalLink size={10} />
              </a>
            )}
          </div>
          <button onClick={onCerrar} className="text-white/40 hover:text-white p-1"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {!nombreAnuncio && (
            <p className="text-amber-300/80 text-[11px]">
              Este anuncio no publica el nombre del propietario — Fotocasa solo lo
              enseña si el dueño lo rellenó. Pónselo tú.
            </p>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <Campo etiqueta="Nombre *" ancho="md:col-span-2">
              <input className={clase} value={f.nombre} onChange={set('nombre')}
                     placeholder="Nombre y apellidos del propietario" />
            </Campo>
            <Campo etiqueta="Teléfono">
              <input className={clase} value={f.telefono} onChange={set('telefono')} />
            </Campo>
            <Campo etiqueta="Email">
              <input className={clase} value={f.email} onChange={set('email')} placeholder="si lo tienes" />
            </Campo>
            <Campo etiqueta="Zona" ancho="md:col-span-2">
              <input className={clase} value={f.zona} onChange={set('zona')} />
            </Campo>
            <Campo etiqueta="Tipo de vivienda">
              <input className={clase} value={f.tipo_vivienda} onChange={set('tipo_vivienda')} />
            </Campo>
            <Campo etiqueta="Precio estimado (€)">
              <input className={clase} type="number" value={f.precio_estimado} onChange={set('precio_estimado')} />
            </Campo>
            <Campo etiqueta="Comisión ofertada (%)"
                   ayuda={ganancia ? `Ganancia calculada: ${ganancia.toLocaleString('es-ES')} €` : null}>
              <input className={clase} type="number" step="0.1" value={f.comision_ofertada}
                     onChange={set('comision_ofertada')} placeholder="3" />
            </Campo>
            <Campo etiqueta="Motivación">
              <select className={clase} value={f.motivacion} onChange={set('motivacion')}>
                {MOTIVACIONES.map(([v, t]) => <option key={v} value={v} className="bg-[#0f172a]">{t}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Estado">
              <select className={clase} value={f.estado} onChange={set('estado')}>
                {ESTADOS.map(([v, t]) => <option key={v} value={v} className="bg-[#0f172a]">{t}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Fecha de visita" ayuda="si ya la tienes cerrada">
              <input className={clase} type="date" value={f.fecha_visita} onChange={set('fecha_visita')} />
            </Campo>
            <Campo etiqueta="Notas" ancho="md:col-span-2"
                   ayuda="Debajo se añade sola la traza del anuncio: portal, enlace y días publicado.">
              <textarea className={clase} rows={3} value={f.notes} onChange={set('notes')}
                        placeholder="Por qué vende, cuándo llamarle, lo que haga falta saber." />
            </Campo>
          </div>

          <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer">
            <input type="checkbox" checked={f.crear_propiedad}
                   onChange={(e) => setF((p) => ({ ...p, crear_propiedad: e.target.checked }))}
                   className="accent-[#c9a84c]" />
            Crear también la propiedad captada con los datos del anuncio
          </label>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/8">
          <button onClick={onCerrar}
            className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white border border-white/10">
            Cancelar
          </button>
          <button onClick={guardar} disabled={guardando}
            className="px-5 py-2 rounded-lg text-sm font-bold text-black disabled:opacity-40 flex items-center gap-2"
            style={{ background: ORO }}>
            {guardando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Crear ficha
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default FichaDesdeAnuncio;
